// Book Description service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (book_description_posting_params)
//   views      app/views/job_postings/steps/book_description/_one.._three.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (book_description__one..three)
//   cost       app/models/job_postings/book_description_posting.rb (#cost / #editing_cost)
//   parity     tasks/post-a-project/parity/enhance-text.md §2
//
// Word count is fixed at 500 (BOOK_DESCRIPTION_WORD_COUNT) server-side; the base
// cost is a flat $94.99 (DESCRIPTION_COST). The Line & Essential editing upgrade
// adds (500/100)*0.8 = $4.00 (NOT $50 — the design hardcoded a wrong figure).
// Subtitle is optional. 3-option AI consent.

import { WRITING_NICHES, WRITING_EDITING_STYLES, RATES } from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

// Design W_EDIT option descriptions (service-config.js). Em dash converted per
// the no-em-dash rule.
const WRITING_EDITING_DESCRIPTIONS = {
  "Essential Editing":
    "Grammar, spelling, syntax, punctuation and inconsistencies, included with every writing package.",
  "Line & Essential Editing":
    "Everything in Essential, plus a line-by-line review for clarity, flow, phrasing and sentence variety.",
}

// WRITING_EDITING_STYLES radio options with the design price text:
// Essential -> "Included", Line & Essential -> "$8.00 / 1,000 words".
// Server-traced via ctx.rates (ruling C2 / tuw-app:P-001), RATES seed as fallback.
const editingOptions = (ctx = {}) =>
  WRITING_EDITING_STYLES.map((style) => {
    // Per-key server-rate read: a null server leaf falls back to the RATES seed
    // for THIS style, never to 0 (tuw-app:P-001). Both styles are always in the
    // seed (Essential 0 / Line & Essential 0.8), so no trailing || 0 is needed.
    const rate = ctx.rates?.writing_editing_styles_cost?.[style] ?? RATES.WRITING_EDITING_STYLES_COST[style]
    return {
      value: style,
      label: style,
      price: rate > 0 ? `$${(rate * 10).toFixed(2)} / 1,000 words` : "Included",
      sub: WRITING_EDITING_DESCRIPTIONS[style],
    }
  })

export const bookDescription = {
  key: "book_description",
  postingKey: "book_description_posting",
  name: "Book Description",

  steps: () => [
    // Step 1 — Book details (book_description__one).
    {
      id: "details",
      title: "Book details",
      sub: "What is the description for?",
      fields: [
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        {
          type: "select",
          key: "niche",
          label: "Niche / genre",
          required: true,
          blankLabel: "Select Niche/Genre",
          options: asOptions([...WRITING_NICHES].sort()),
        },
        {
          type: "textarea",
          key: "additional_info",
          label: "Additional information",
          required: false,
          rows: 4,
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          required: false,
          accept: ".doc,.docx,.pdf,.zip",
          placeholder: "Upload a file",
        },
      ],
    },

    // Step 2 — Editing style (book_description__two).
    {
      id: "editing",
      title: "Editing style",
      sub: "Editing is included. Pick the level.",
      fields: [
        {
          type: "radio",
          key: "editing_style",
          required: true,
          options: (v = {}, ctx = {}) => editingOptions(ctx),
        },
      ],
    },

    // Step 3 — AI use (book_description__three -> _ai_consent). 3-option consent.
    {
      id: "ai",
      title: "AI use in your project",
      sub: "Choose how much your writer may use AI. Please select one.",
      fields: [
        {
          type: "consent",
          key: "ai_consent",
          mode: "3",
          label: "AI use in your project",
          required: true,
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby (book_description_posting.rb#cost):
  //   DESCRIPTION_COST (flat 94.99) + editing_cost
  //   editing_cost = (500 / 100.0) * rate  (rate = 0.8 Line, 0 Essential) => $4.00 / $0.
  estimate: (values = {}, ctx = {}) => {
    const base = ctx.rates?.description_cost ?? RATES.DESCRIPTION_COST
    const lines = [{ label: "Book description", amount: money(base) }]

    const editRate = ctx.rates?.writing_editing_styles_cost?.[values.editing_style] ?? RATES.WRITING_EDITING_STYLES_COST[values.editing_style] ?? 0
    const editAdd = (RATES.BOOK_DESCRIPTION_WORD_COUNT / 100) * editRate

    if (editAdd > 0) {
      lines.push({ label: "Line editing upgrade", amount: money(editAdd) })
    }

    const total = money(lines.reduce((sum, l) => sum + l.amount, 0))

    return { lines, total, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // book_description_posting = [editing_style, quality_assurance, quality_control].
  // The base is flat so the service is always priceable. QA/QC are JOB-LEVEL
  // singletons the engine layers onto this eligible service (not in values/ctx).
  pricePayload: (values = {}) => ({
    postingType: "book_description_posting",
    editingStyle: values.editing_style ?? null,
  }),
}
